import type { FlowType, PaymentMode } from '../types'
import type { JobIconName } from './jobTypes'

/**
 * Conversion events for the garage's analytics and AutoChain's bookings funnel (ACA-107).
 *
 * Each step of a visit is reported once: to the host page's Google Tag Manager
 * (`window.dataLayer`), to Google Analytics (`gtag`) when the page has it, to
 * the page around the widget when it runs in a frame, and to AutoChain, which
 * keeps only anonymous daily counts.
 *
 * Nothing personal ever leaves in an event: no name, email, phone,
 * registration, VIN, booking id or free text. Every value comes from a fixed
 * list (steps, job categories, price bands, payment modes) or is the traffic
 * source, which is checked against a narrow shape first.
 */

export const CONVERSION_STEPS = [
  'widget_opened',
  'vehicle_found',
  'service_chosen',
  'time_chosen',
  'details_entered',
  'booking_created',
  'payment_completed',
] as const

export type ConversionStep = (typeof CONVERSION_STEPS)[number]

export const CONVERSION_EVENT_PREFIX = 'autochain_'

/** The `type` of the message a framed widget posts to the page around it. */
export const CONVERSION_MESSAGE_TYPE = 'autochain:booking-widget-event'

export type PriceBand = 'none' | 'under_50' | '50_99' | '100_249' | '250_499' | '500_plus'

export type BookingOutcome = 'request' | 'confirmed' | 'awaiting_payment'

export type VehicleOutcome = 'found' | 'entered' | 'skipped' | 'size'

/** What an event may say beyond its name and source. Every value is from a fixed list. */
export interface ConversionDetails {
  flow?: FlowType
  serviceCategories?: JobIconName[]
  priceBand?: PriceBand
  paymentMode?: PaymentMode
  bookingStatus?: BookingOutcome
  vehicle?: VehicleOutcome
}

/** The flat parameters an event carries, as GA4 and GTM expect them. */
export interface ConversionParams {
  source: string
  flow?: FlowType
  service_categories?: string
  price_band?: PriceBand
  payment_mode?: PaymentMode
  booking_status?: BookingOutcome
  vehicle?: VehicleOutcome
}

export const DIRECT_SOURCE = 'direct'
export const OTHER_SOURCE = 'other'

const SOURCE_SHAPE = /^[a-z0-9][a-z0-9._-]{0,39}$/
/** A run of digits that long could be a phone number. */
const LONG_DIGIT_RUN = /\d{7,}/

/**
 * A traffic source as AutoChain stores it: lower case letters, digits, dot,
 * dash and underscore, at most 40 characters. Missing is "direct"; anything
 * else, including anything that could be an email address or a phone number,
 * is "other". Mirrors WidgetFunnelService.normaliseSource on the server.
 */
export function normaliseSource(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || !raw.trim()) return DIRECT_SOURCE
  const value = raw.trim().toLowerCase()
  if (!SOURCE_SHAPE.test(value) || LONG_DIGIT_RUN.test(value)) return OTHER_SOURCE
  return value
}

/**
 * Where the visit came from: the embed's own `trafficSource` (the hosted
 * booking page sets it from its link), else `utm_source` on the page the
 * widget runs on, else direct.
 */
export function resolveTrafficSource(
  configured: string | undefined,
  win: Pick<Window, 'location'> | null = typeof window === 'undefined' ? null : window
): string {
  if (typeof configured === 'string' && configured.trim()) return normaliseSource(configured)
  try {
    const search = win?.location?.search ?? ''
    return normaliseSource(new URLSearchParams(search).get('utm_source'))
  } catch {
    return DIRECT_SOURCE
  }
}

/** A total in pounds as a band, never the amount itself. */
export function priceBand(total: number | null | undefined): PriceBand {
  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) return 'none'
  if (total < 50) return 'under_50'
  if (total < 100) return '50_99'
  if (total < 250) return '100_249'
  if (total < 500) return '250_499'
  return '500_plus'
}

const JOB_ICON_NAMES: ReadonlySet<string> = new Set<JobIconName>([
  'servicing',
  'mot',
  'brakes',
  'tyres',
  'aircon',
  'diagnostics',
  'battery',
  'exhaust',
  'clutch',
  'suspension',
  'windscreen',
  'wheels',
  'repairs',
  'other',
])
const FLOWS: ReadonlySet<string> = new Set<FlowType>(['general', 'tyres', 'mot'])
const PRICE_BANDS: ReadonlySet<string> = new Set<PriceBand>([
  'none',
  'under_50',
  '50_99',
  '100_249',
  '250_499',
  '500_plus',
])
const PAYMENT_MODES: ReadonlySet<string> = new Set<PaymentMode>(['none', 'deposit', 'full'])
const BOOKING_OUTCOMES: ReadonlySet<string> = new Set<BookingOutcome>([
  'request',
  'confirmed',
  'awaiting_payment',
])
const VEHICLE_OUTCOMES: ReadonlySet<string> = new Set<VehicleOutcome>([
  'found',
  'entered',
  'skipped',
  'size',
])

/**
 * The parameters for one event. Built from an allow-list, so a value that is
 * not one of the known ones is dropped rather than passed on.
 */
export function conversionParams(source: string, details: ConversionDetails = {}): ConversionParams {
  const params: ConversionParams = { source: normaliseSource(source) }
  if (details.flow && FLOWS.has(details.flow)) params.flow = details.flow
  if (details.serviceCategories) {
    const categories = [...new Set(details.serviceCategories)]
      .filter((name) => JOB_ICON_NAMES.has(name))
      .sort()
    if (categories.length > 0) params.service_categories = categories.join(',')
  }
  if (details.priceBand && PRICE_BANDS.has(details.priceBand)) params.price_band = details.priceBand
  if (details.paymentMode && PAYMENT_MODES.has(details.paymentMode)) {
    params.payment_mode = details.paymentMode
  }
  if (details.bookingStatus && BOOKING_OUTCOMES.has(details.bookingStatus)) {
    params.booking_status = details.bookingStatus
  }
  if (details.vehicle && VEHICLE_OUTCOMES.has(details.vehicle)) params.vehicle = details.vehicle
  return params
}

function originOf(href: string): string | null {
  try {
    const url = new URL(href)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null
  } catch {
    return null
  }
}

function isFramed(win: Window): boolean {
  try {
    return win.parent !== win.self
  } catch {
    return true
  }
}

/**
 * The origin of the page directly around the widget's frame, or null when it
 * is not framed or nothing says. Read directly when that page is on the same
 * origin, else from the browser's list of ancestor origins, else from the
 * referrer the frame was loaded with (unless that is the frame's own site,
 * which means a link followed inside the frame).
 */
export function parentOrigin(win: Window = window): string | null {
  if (!isFramed(win)) return null

  try {
    const origin = win.parent.location.origin
    if (typeof origin === 'string' && originOf(origin)) return origin
  } catch {
    // Another origin: fall through.
  }

  const ancestors = win.location.ancestorOrigins
  if (ancestors && ancestors.length > 0) {
    const nearest = originOf(ancestors[0])
    if (nearest) return nearest
  }

  const referrer = win.document?.referrer ?? ''
  const referrerOrigin = referrer ? originOf(referrer) : null
  if (referrerOrigin && referrerOrigin !== win.location.origin) return referrerOrigin
  return null
}

type AnalyticsWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

/**
 * Hands one event to the page's analytics. Each channel is tried on its own:
 * a broken tag on the garage's site must never stop the booking.
 */
export function publishConversion(
  step: ConversionStep,
  params: ConversionParams,
  win: Window = window
): void {
  const event = `${CONVERSION_EVENT_PREFIX}${step}`
  const analytics = win as AnalyticsWindow

  try {
    if (!Array.isArray(analytics.dataLayer)) analytics.dataLayer = []
    analytics.dataLayer.push({ event, ...params })
  } catch {
    // A page whose dataLayer cannot be written to has no tag manager to tell.
  }

  try {
    if (typeof analytics.gtag === 'function') analytics.gtag('event', event, { ...params })
  } catch {
    // Same: the garage's analytics never break the booking.
  }

  const target = parentOrigin(win)
  if (!target) return
  try {
    win.parent.postMessage({ type: CONVERSION_MESSAGE_TYPE, event, params: { ...params } }, target)
  } catch {
    // The page around the frame went away or refused the message.
  }
}

export interface ConversionTracker {
  /** Reports the step unless this visit already has. True when it was reported now. */
  track: (step: ConversionStep, details?: ConversionDetails) => boolean
  /** Treat steps as already reported, e.g. on the page Checkout returns to. */
  markReported: (steps: readonly ConversionStep[]) => void
  hasReported: (step: ConversionStep) => boolean
}

export interface ConversionTrackerOptions {
  source: string
  win?: Window
  /** Also told about each step, e.g. to send it to AutoChain's funnel. Errors are ignored. */
  onStep?: (step: ConversionStep, params: ConversionParams) => void
}

/** One per widget visit: each step is reported at most once. */
export function createConversionTracker(options: ConversionTrackerOptions): ConversionTracker {
  const reported = new Set<ConversionStep>()

  return {
    track(step, details) {
      if (reported.has(step)) return false
      reported.add(step)

      const params = conversionParams(options.source, details)
      publishConversion(step, params, options.win ?? window)
      try {
        options.onStep?.(step, params)
      } catch {
        // Counting is best effort.
      }
      return true
    },
    markReported(steps) {
      for (const step of steps) reported.add(step)
    },
    hasReported(step) {
      return reported.has(step)
    },
  }
}
